import type { ChartDataDigest } from './turn-outcome-digest';
import {
  chartSummary,
  NATURAL_METRIC_NAME_INSTRUCTION,
} from './turn-outcome-digest';

/**
 * Every `[System: …] ${prompt}` seed the assistant fires as a SILENT resumed
 * turn, in ONE module.
 *
 * These seeds are model-facing prompt text: prompt copies that drift are a
 * recurring defect class here, and a drifted copy changes assistant behavior
 * on only one of its routes — which is exactly the kind of split that is
 * near-impossible to reproduce from a bug report. Keeping every seed here
 * makes each one a single source of truth and the full "what can the assistant
 * be silently told" surface reviewable in one place.
 */

/**
 * The system seed for a nav-confirm resume turn.
 *
 * Two call sites reach the identical model-facing state by different routes:
 * `use-global-ai-offer-wiring` fires it inline when the user was ALREADY on the
 * destination (no navigation, so no location change to key off), and
 * `use-global-ai-turn-continuation` fires it from the location-keyed effect once
 * the router lands. The user cannot tell the two apart, so the model must not
 * either.
 */
export function buildNavResumeSeed({
  pageName,
  continuationPrompt,
}: {
  pageName: string;
  continuationPrompt: string;
}): string {
  return `[System: The user clicked the confirmation button and you have ALREADY navigated to ${pageName}. The navigation is done — do NOT navigate again or offer another navigation button. If the request was only to open this page, briefly confirm you are here and end your turn; otherwise continue any remaining task on this page.] ${continuationPrompt}`;
}

/**
 * The system seed for the BUILD-HERE resume: the "Build the dashboard" click
 * created an empty draft + navigated to it, and the resumed turn must build
 * the draft out via `create_custom_page` (CREATE framing) rather than offer
 * again. Fired by `use-global-ai-offer-wiring`, either immediately (already
 * on the draft path) or armed as a `create_page_resume` continuation.
 */
export function buildBuildHereResumeSeed(prompt: string): string {
  return `[System: The user clicked "Build the dashboard", which navigated you to a NEW EMPTY editable custom dashboard. Build it out ON THIS PAGE now by calling create_custom_page with their request — it will populate THIS page. create_custom_page's narration input still requires an [phrase](offer:build) marker: pass a short note like "[Building the dashboard](offer:build)" — it is not shown to the user. Do NOT navigate, create another page, call update_current_page, render a single chart, offer another button, or offer a report (a report was already offered with the build button). After the page is built, narrate it and end your turn.] ${prompt}`;
}

/**
 * The system seed for the resolve-first build's landing narration (see
 * `seeded-build-narration.ts` for the flow): a silent narrate-only turn
 * grounded in the seeded page's collected widget digests, tiered by how much
 * data has settled so the model never invents chart contents.
 */
export function buildSeededBuildNarrationSeed(params: {
  prompt: string;
  digests: ChartDataDigest[];
  expectedCount: number;
}): string {
  const base = `The user clicked the build option and the PRE-GENERATED dashboard is ALREADY on screen as a new draft page — the build is DONE. Do NOT navigate, create or update any page, render a chart, apply filters, or offer any button or report. ${NATURAL_METRIC_NAME_INSTRUCTION}`;
  if (params.digests.length === 0) {
    return `[System: ${base} No chart data has settled yet — do not describe specific charts or claim the page covers any particular metric; give ONE short sentence saying the dashboard is ready and its data is loading, then end your turn.] ${params.prompt}`;
  }
  const summaries = params.digests.map(chartSummary).join('\n---\n');
  if (params.digests.length < params.expectedCount) {
    return `[System: ${base} The page has ${params.expectedCount} charts; data has settled for ${params.digests.length} of them so far (the rest are still loading):\n${summaries}\n\nGive a brief grounded summary (1–2 sentences) leading with the concrete figures above, referring to charts by their titles, and mention the remaining charts are still loading. Do NOT invent contents for charts not listed — never name their metrics or values. Frame it as a NEWLY BUILT dashboard, not an update. Then end your turn.] ${params.prompt}`;
  }
  return `[System: ${base} These are the ONLY charts on the page — narrate ONLY these:\n${summaries}\n\nGive a brief grounded summary (1–2 sentences) that refers to these charts by their titles and leads with the concrete figures above. Do NOT mention any metric, theme, or section that is not one of the charts listed. Frame it as a NEWLY BUILT dashboard, not an update. Then end your turn.] ${params.prompt}`;
}

/**
 * The system seed for the post-loop report ride-along resume: the turn
 * produced an analytical outcome, `select-report` matched prebuilt report(s),
 * and the loop is resumed so the MODEL makes the validated `offer_report`
 * call (explained narration with inline offer markers) instead of the
 * deterministic card fallback. Fired by `use-global-ai-turn-runner`.
 *
 * The runner blocks every CLIENT tool but offer_report; server reads still
 * run, and a read-first routing rule re-grounded the answer here and put its
 * figures in the narration a second time.
 */
export function buildReportOfferResumeSeed(params: {
  /** Pre-joined "Name — description (reportType: x)" lines, '; '-separated. */
  reportLines: string;
  reportTypes: string[];
  userPrompt: string;
}): string {
  return `[System: The user's request matches prebuilt report(s): ${params.reportLines}. Offer them NOW by calling offer_report ONCE with reportTypes [${params.reportTypes.join(', ')}], prompt set to the user's request, and a narration of 1–2 grounded sentences per report explaining why it fits this request and what the report contains, each clause ending on its [<Report Name>](offer:report:<reportType>) marker (one marker per report, in reportTypes order, naming that report's reportType). The GLOBAL CAUSAL-CLAIM BAN applies to this narration: describe what each report CONTAINS, never assert that the user's outcome is caused by / driven by / due to / "traces to" anything. Do NOT render charts, navigate, build pages, ask questions, or emit free text — offer_report is the only action available. The answer above already stated the figures: call no search_filters or read, and restate no figure in this narration.] ${params.userPrompt}`;
}
